import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  CACHE_KEYS,
  CreateOrUpdateInvestmentParams,
  Investment,
  useEditInvestment,
  useListAndSearchAllTags,
} from "@rentablo/api/apiv1";
import { AccountTypeEnum } from "@api";
import { useAccounts } from "@api/v1/hooks/useAccounts";
import { InvestmentEditBasicTabContainer } from "./InvestmentEditBasicTabContainer";

interface Props {
  investment: Investment;
}

export const InvestmentEditBasicTab = ({ investment }: Props) => {
  const { t } = useTranslation();
  const depotType: AccountTypeEnum =
    investment.type === "61_pmetals" || investment.type === "71_massets"
      ? "05_material_assets"
      : "01_depot";

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: { accounts = [] } = { accounts: [] } } = useAccounts({
    type: depotType,
  });
  const { data: tagsResponse } = useListAndSearchAllTags();
  const { mutateAsync: update, isPending } = useEditInvestment();

  const handleSave = (data: CreateOrUpdateInvestmentParams) => {
    update(
      {
        id: investment.id,
        createOrUpdateInvestmentParams: data,
      },
      {
        onSuccess: async () => {
          enqueueSnackbar(
            `${t("investmentEditPage.basicTab.saved")}: ${data.name}`,
            { autoHideDuration: 3000, variant: "success" }
          );
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [CACHE_KEYS.INVESTMENTS_ID],
            }),
            queryClient.invalidateQueries({
              queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_TAGS],
            }),
          ]);
        },
      }
    );
  };

  return (
    <InvestmentEditBasicTabContainer
      investment={investment}
      depots={accounts}
      tags={tagsResponse?.tags ?? []}
      isSaving={isPending}
      onSave={handleSave}
    />
  );
};
